import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import type { KanbanColumnDTO } from "@unik/shared";
import { api } from "../lib/api";
import { Button } from "./Button";
import { Input } from "./Input";

interface QuickClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultStage?: string;
  columns: KanbanColumnDTO[];
}

export function QuickClientModal({
  isOpen,
  onClose,
  onCreated,
  defaultStage = "novo",
  columns,
}: QuickClientModalProps) {
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [tier, setTier] = useState<"mentoria" | "consultoria" | "imersao">("mentoria");
  const [stage, setStage] = useState(defaultStage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setBusinessName("");
      setWhatsapp("");
      setTier("mentoria");
      setStage(defaultStage || (columns[0]?.key ?? "novo"));
      setError(null);
    }
  }, [isOpen, defaultStage, columns]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() === "") {
      setError("Informe o nome do cliente.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post("/clients", {
        name: name.trim(),
        businessName: businessName.trim() || null,
        whatsapp: whatsapp.trim() || null,
        tier,
        status: "active",
        kanbanStage: stage,
      });

      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Não foi possível cadastrar o cliente.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-md rounded-[var(--radius-card)] border border-border-default bg-[#180b0b]/98 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border-subtle/80 pb-4">
          <div className="flex items-center gap-2">
            <span className="text-gold">✦</span>
            <h2 className="type-h3 text-text-cream">Adicionar Novo Card</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-text-tertiary hover:bg-surface-elevated hover:text-text-primary transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {error && <p className="type-caption text-danger">{error}</p>}

          <Input
            label="Nome do Cliente *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Dra. Camila Rezende"
            autoFocus
          />

          <Input
            label="Clínica / Empresa"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Ex.: Instituto Dermatologia Avançada"
          />

          <Input
            label="WhatsApp"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="Ex.: (11) 99999-8888"
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="type-label block text-xs text-text-secondary mb-1">
                Oferta / Tier
              </label>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value as "mentoria" | "consultoria" | "imersao")}
                className="w-full h-10 rounded-[var(--radius-control)] border border-border-default bg-surface-base px-3 text-xs text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="mentoria">Mentoria 1:1</option>
                <option value="consultoria">Consultoria</option>
                <option value="imersao">Imersão</option>
              </select>
            </div>

            <div>
              <label className="type-label block text-xs text-text-secondary mb-1">
                Coluna Inicial
              </label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="w-full h-10 rounded-[var(--radius-control)] border border-border-default bg-surface-base px-3 text-xs text-text-primary focus:border-gold focus:outline-none"
              >
                {columns.map((c) => (
                  <option key={c.id} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2 pt-2 border-t border-border-subtle/60">
            <Button variant="ghost" size="sm" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button size="sm" type="submit" loading={loading}>
              Criar Card
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
